import { option, string } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { useEffect, useMemo, useState } from 'react';
import { match } from 'ts-pattern';

import { ADD_SUBSCRIBER_ENDPOINT } from 'api/ListsApi';
import { useRestMutation } from 'utils/hooks/api/useRestMutation';
import { useUuidBasedProject } from 'utils/hooks/useUuidBasedProject';

import { List } from '../domain';

type SubscribeToListRequest = {
  emailListId: number;
  subscriber: { email?: string; userId?: string };
};

type UserIdSubscriber = { userId: string };
type EmailSubscriber = { email: string };
type Subscriber = UserIdSubscriber | EmailSubscriber;

const getSubscriber = (
  email: string,
  userId: string,
  isEmailUnique: boolean,
  isEmailNonUnique: boolean,
): Subscriber =>
  match<undefined, Subscriber>(undefined)
    .when(
      () => isEmailNonUnique,
      () => ({ userId }),
    )
    .when(
      () => isEmailUnique && !string.isEmpty(userId),
      () => ({ userId }),
    )
    .otherwise(() => ({ email }));

const getPayload = (subscriber: Subscriber, list?: List): SubscribeToListRequest | undefined =>
  pipe(
    list,
    option.fromNullable,
    option.map((l) => l.id),
    option.map((emailListId) => ({ emailListId })),
    option.map((payload) => ({
      ...payload,
      subscriber,
    })),
    option.toUndefined,
  );

export const useSubscribeToList = (email = '', userId = '') => {
  const { isEmailUnique, isEmailNonUnique } = useUuidBasedProject();
  const [toList, subscribe] = useState<List>();
  const { mutate, isError, isSuccess, isLoading, reset } = useRestMutation<
    unknown,
    SubscribeToListRequest
  >(ADD_SUBSCRIBER_ENDPOINT);

  const payload = useMemo(
    () => getPayload(getSubscriber(email, userId, isEmailUnique, isEmailNonUnique), toList),
    [email, userId, isEmailUnique, isEmailNonUnique, toList],
  );

  useEffect(() => {
    if (payload) {
      mutate(payload);
    }
  }, [mutate, payload]);

  return { subscribe, isError, isSuccess, isLoading, toList, reset };
};
